/**
 * Stage of a permit procedure in the page header (ADR 011): the phases as small tabs, below them
 * the stations of the phase shown as dots on one line.
 *
 * The phases are the two processes, Antragserstellung and Verfahren; Bestandskräftig is the last
 * station of the procedure, not a phase of its own. The tab of the current phase is open; another
 * tab shows that phase without changing anything. Stations, phases and branches come from the
 * server (`status_steps`).
 *
 * Three looks: done (filled, line in the accent up to the current station), current (white with
 * a glow), open (outlined). A side branch (Nachforderung, Anfechtung) hangs below its station as
 * a chip; while in preparation, the current station carries the evidence count.
 *
 * The main action sits on the line instead of the dot of the station it leads to – a white pill
 * with an arrow, the station and the deadline of the current status below it. Leads it to the
 * next phase, it stands at the end of the line. The other actions are in the menu at the top
 * right. A withdrawn procedure has no place in the flow; it shows the notice and the way back.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import {
  ArrowForward as ArrowIcon,
  Check as CheckIcon,
  MoreHoriz as MoreIcon,
} from "@mui/icons-material";
import { colors, fonts } from "../../../../styles/theme";
import { formatDate } from "../../../../utils/formatters";
import { PermitProcedure, ProcedureAction } from "../../../../types";
import { mainAction } from "./useProcedureActions";

interface ProcedureStageProps {
  procedure: PermitProcedure;
  /** Main action and menu only when the procedure may be changed */
  editable: boolean;
  onAction: (action: ProcedureAction) => void;
  onOpenMenu: (anchor: HTMLElement) => void;
}

/** "3 / 6 Nachweise abgeschlossen"; without evidences nothing */
const evidenceCount = (procedure: PermitProcedure): string | undefined => {
  const total = procedure.evidences_total;
  if (!total) return undefined;
  return `${procedure.evidences_completed} / ${total} ${total === 1 ? "Nachweis" : "Nachweise"} abgeschlossen`;
};

const deadlineText = (procedure: PermitProcedure): string | undefined =>
  procedure.deadline
    ? `${procedure.deadline.label} ${formatDate(procedure.deadline.due)}`
    : undefined;

type StationState = "done" | "current" | "open";

interface Station {
  key: string;
  label: string;
  state: StationState;
  /** Side branch the procedure is in at this station */
  branch?: string;
  hint?: string;
  /** The main action sits here instead of the dot */
  action?: ProcedureAction;
}

/** Dot size (px); the line runs through its middle */
const DOT = 16;

/** The main action on the line: white pill with an arrow, clearly a button */
const ActionPill: React.FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick,
}) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      // Centered on the line: half its height above the dot's middle
      mt: "-8px",
      mb: 1,
      gap: 0.75,
      pl: 1.75,
      pr: 0.75,
      py: 0.75,
      borderRadius: 999,
      backgroundColor: colors.pageHeader.tabsBackground,
      color: colors.pageHeader.gradientMiddle,
      fontSize: "0.8rem",
      fontWeight: fonts.weights.bold,
      whiteSpace: "nowrap",
      boxShadow: `0 0 0 4px ${colors.pageHeader.dotCurrentRing}, 0 6px 18px ${colors.pageHeader.buttonShadow}`,
      transition: "transform 150ms ease, box-shadow 150ms ease",
      "&:hover, &:focus-visible": {
        transform: "scale(1.04)",
        boxShadow: `0 0 0 6px ${colors.pageHeader.dotCurrentRing}, 0 8px 22px ${colors.pageHeader.buttonShadow}`,
      },
    }}
  >
    {label}
    <Box
      component="span"
      sx={{
        display: "grid",
        placeItems: "center",
        width: 20,
        height: 20,
        borderRadius: "50%",
        color: colors.pageHeader.text,
        background: `linear-gradient(120deg, ${colors.pageHeader.gradientEnd}, ${colors.pageHeader.lineDone})`,
      }}
    >
      <ArrowIcon sx={{ fontSize: 13 }} />
    </Box>
  </ButtonBase>
);

const ProcedureStage: React.FC<ProcedureStageProps> = ({
  procedure,
  editable,
  onAction,
  onOpenMenu,
}) => {
  const steps = procedure.status_steps;
  // The station the procedure is shown at: its status, or the station its side branch hangs at.
  const stationIndex = steps.findIndex(
    (step) =>
      step.status === procedure.status ||
      step.branch_status === procedure.status,
  );
  const phases = useMemo(
    () => [...new Set(steps.map((step) => step.phase_label))],
    [steps],
  );
  const currentPhase = steps[stationIndex]?.phase_label ?? phases[0];
  const [shownPhase, setShownPhase] = useState(currentPhase);
  // A transition into the next phase opens it.
  useEffect(() => setShownPhase(currentPhase), [currentPhase]);

  const action = editable ? mainAction(procedure) : null;
  const menu = editable && !procedure.withdrawn && (
    <Tooltip title="Weitere Aktionen">
      <IconButton
        aria-label="Weitere Aktionen"
        size="small"
        onClick={(event) => onOpenMenu(event.currentTarget)}
        sx={{
          position: "absolute",
          top: -4,
          right: -8,
          color: colors.pageHeader.textSecondary,
        }}
      >
        <MoreIcon />
      </IconButton>
    </Tooltip>
  );

  if (procedure.withdrawn) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}>
        <Typography variant="body2" sx={{ color: colors.pageHeader.accentLight }}>
          Der Antrag ist zurückgenommen, zuletzt im Status „
          {procedure.status_label}“.
        </Typography>
        {action && (
          <Box sx={{ pt: 1 }}>
            <ActionPill label={action.label} onClick={() => onAction(action)} />
          </Box>
        )}
      </Box>
    );
  }

  const phaseRank = (phase: string) => phases.indexOf(phase);
  const shownRank = phaseRank(shownPhase);
  const currentRank = phaseRank(currentPhase);
  const shownSteps = steps.filter((step) => step.phase_label === shownPhase);
  // In an earlier phase every station is done, in a later one every station is open.
  const current =
    shownRank < currentRank
      ? shownSteps.length
      : shownRank > currentRank
        ? -1
        : shownSteps.findIndex((step) => step === steps[stationIndex]);

  const stations: Station[] = shownSteps.map((step, index) => ({
    key: step.status,
    label: step.label,
    state: index < current ? "done" : index === current ? "current" : "open",
    branch:
      index === current && step.branch_label ? step.branch_label : undefined,
    hint:
      index === current && step.status === "in_preparation"
        ? evidenceCount(procedure)
        : undefined,
  }));

  // The main action goes to the station it leads to; out of the shown phase (into the next one)
  // it stands at the end of the line – but only in the phase the procedure is in.
  if (action && shownRank === currentRank) {
    const target = stations.findIndex(
      (station) => station.key === action.target_status,
    );
    if (target > current) {
      stations[target] = { ...stations[target], action };
    } else if (target === -1) {
      const targetStep = steps.find(
        (step) => step.status === action.target_status,
      );
      stations.push({
        key: `action-${action.action}`,
        label: targetStep?.label ?? "",
        state: "open",
        action,
      });
    }
  }
  const deadline = deadlineText(procedure);
  const deadlineAtAction = stations.some((station) => station.action);

  return (
    <Box sx={{ position: "relative" }}>
      {menu}
      <Box role="tablist" sx={{ display: "flex", gap: 2.25, mb: 1.75 }}>
        {phases.map((phase) => {
          const rank = phaseRank(phase);
          const selected = phase === shownPhase;
          return (
            <ButtonBase
              key={phase}
              role="tab"
              aria-selected={selected}
              onClick={() => setShownPhase(phase)}
              sx={{
                gap: 0.5,
                pb: 0.25,
                fontSize: "0.8rem",
                fontWeight: selected
                  ? fonts.weights.semiBold
                  : fonts.weights.regular,
                color: selected
                  ? colors.pageHeader.text
                  : colors.pageHeader.textSecondary,
                borderBottom: `2px solid ${selected ? colors.pageHeader.lineDone : "transparent"}`,
              }}
            >
              {rank < currentRank && <CheckIcon sx={{ fontSize: 14 }} />}
              {phase}
            </ButtonBase>
          );
        })}
      </Box>

      <Box
        role="list"
        aria-label={`Status: ${procedure.status_label}`}
        sx={{ display: "flex", alignItems: "flex-start" }}
      >
        {stations.map((station, index) => {
          const lineDone = index <= current;
          return (
            <Box
              key={station.key}
              role="listitem"
              aria-current={station.state === "current" ? "step" : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                fontSize: "0.75rem",
                color:
                  station.state === "open"
                    ? colors.pageHeader.textMuted
                    : colors.pageHeader.text,
                fontWeight:
                  station.state === "current" || station.action
                    ? fonts.weights.semiBold
                    : fonts.weights.regular,
                // Line from the previous station to this one
                "&::before":
                  index === 0
                    ? undefined
                    : {
                        content: '""',
                        position: "absolute",
                        top: DOT / 2 - 1,
                        // From the edge of the previous dot to the edge of this one: open dots
                        // are rings, the gradient shows through them.
                        left: `calc(-50% + ${DOT / 2}px)`,
                        width: `calc(100% - ${DOT}px)`,
                        height: 2,
                        backgroundColor: lineDone
                          ? colors.pageHeader.lineDone
                          : colors.pageHeader.line,
                      },
              }}
            >
              {station.action ? (
                <ActionPill
                  label={station.action.label}
                  onClick={() => onAction(station.action as ProcedureAction)}
                />
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    width: DOT,
                    height: DOT,
                    mb: 1,
                    borderRadius: "50%",
                    border: `2px solid ${
                      station.state === "done"
                        ? colors.pageHeader.dotDone
                        : station.state === "current"
                          ? colors.pageHeader.text
                          : colors.pageHeader.dotBorder
                    }`,
                    backgroundColor:
                      station.state === "done"
                        ? colors.pageHeader.dotDone
                        : station.state === "current"
                          ? colors.pageHeader.text
                          : "transparent",
                    boxShadow:
                      station.state === "current"
                        ? `0 0 0 5px ${colors.pageHeader.dotCurrentRing}, 0 0 18px ${colors.pageHeader.textSecondary}`
                        : "none",
                  }}
                />
              )}
              <Box sx={{ px: 0.5, lineHeight: 1.35 }}>{station.label}</Box>
              {station.branch && (
                <Box
                  sx={{
                    mt: 0.5,
                    px: 1,
                    py: 0.125,
                    borderRadius: 999,
                    fontWeight: fonts.weights.semiBold,
                    color: colors.pageHeader.gradientStart,
                    backgroundColor: colors.pageHeader.accentLight,
                  }}
                >
                  {station.branch}
                </Box>
              )}
              {(station.hint ||
                (deadline &&
                  (station.action ||
                    (!deadlineAtAction && station.state === "current")))) && (
                <Box
                  sx={{
                    mt: 0.25,
                    fontWeight: fonts.weights.regular,
                    color: colors.pageHeader.textSecondary,
                  }}
                >
                  {station.hint ?? deadline}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProcedureStage;
